/**
 * updateWaterwaysLayer — Manages NHD rivers/streams/lakes from vector tiles.
 * Extracted from useMapLayers for file size reduction.
 */
import { WATERWAYS_TILESET } from '@/utils/mapConstants';

export function updateWaterwaysLayer(map, showRivers, showLakes, forceRecreate = false) {
  if (!map) return;

  const riverLayers = ['waterway-highlight', 'waterways-flowlines-label', 'waterways-flowlines'];
  const lakeLayers = ['waterbody-highlight-outline', 'waterbody-highlight', 'waterways-waterbodies-outline', 'waterways-waterbodies'];
  const allLayers = [...riverLayers, ...lakeLayers];

  try {
    if (forceRecreate) {
      allLayers.forEach(layerId => { if (map.getLayer(layerId)) map.removeLayer(layerId); });
      if (map.getSource('waterways')) map.removeSource('waterways');
    }

    if (!showRivers) {
      riverLayers.forEach(layerId => { if (map.getLayer(layerId)) map.setLayoutProperty(layerId, 'visibility', 'none'); });
    }
    if (!showLakes) {
      lakeLayers.forEach(layerId => { if (map.getLayer(layerId)) map.setLayoutProperty(layerId, 'visibility', 'none'); });
    }

    if (!showRivers && !showLakes) return;

    if (!map.getSource('waterways')) {
      map.addSource('waterways', { type: 'vector', url: WATERWAYS_TILESET });
    }

    const firstMarkerLayer = map.getLayer('destinations-layer') ? 'destinations-layer'
      : map.getLayer('pois-layer') ? 'pois-layer'
      : map.getLayer('pois-circles') ? 'pois-circles'
      : undefined;

    // === LAKES LAYERS ===
    if (showLakes) {
      if (!map.getLayer('waterways-waterbodies')) {
        try {
          map.addLayer({
            id: 'waterways-waterbodies', type: 'fill', source: 'waterways', 'source-layer': 'waterbodies', minzoom: 6,
            paint: { 'fill-color': '#0EA5E9', 'fill-opacity': 0.25 },
            layout: { 'visibility': 'visible' }
          }, firstMarkerLayer);
        } catch (e) {}
      } else {
        map.setLayoutProperty('waterways-waterbodies', 'visibility', 'visible');
      }

      if (!map.getLayer('waterways-waterbodies-outline')) {
        map.addLayer({
          id: 'waterways-waterbodies-outline', type: 'line', source: 'waterways', 'source-layer': 'waterbodies', minzoom: 6,
          paint: { 'line-color': '#0284C7', 'line-width': 1 },
          layout: { 'visibility': 'visible' }
        }, firstMarkerLayer);
      } else {
        map.setLayoutProperty('waterways-waterbodies-outline', 'visibility', 'visible');
      }

      if (!map.getLayer('waterbody-highlight')) {
        map.addLayer({
          id: 'waterbody-highlight', type: 'fill', source: 'waterways', 'source-layer': 'waterbodies',
          filter: ['==', 'Permanent_Identifier', ''],
          paint: { 'fill-color': '#0EA5E9', 'fill-opacity': 0.45 },
          layout: { 'visibility': 'visible' }
        });
      }

      if (!map.getLayer('waterbody-highlight-outline')) {
        map.addLayer({
          id: 'waterbody-highlight-outline', type: 'line', source: 'waterways', 'source-layer': 'waterbodies',
          filter: ['==', 'Permanent_Identifier', ''],
          paint: { 'line-color': '#F97316', 'line-width': 3, 'line-opacity': 0.9 },
          layout: { 'visibility': 'visible' }
        });
      }
    }

    // === RIVERS LAYERS ===
    if (showRivers) {
      if (!map.getLayer('waterways-flowlines')) {
        try {
          map.addLayer({
            id: 'waterways-flowlines', type: 'line', source: 'waterways', 'source-layer': 'flowlines', minzoom: 4,
            filter: ['has', 'GNIS_Name'],
            layout: { 'line-join': 'round', 'line-cap': 'round', 'visibility': 'visible' },
            paint: {
              'line-color': '#0EA5E9',
              'line-width': [
                'interpolate', ['linear'], ['zoom'],
                4, ['match', ['get', 'StreamOrde'], 1, 0.2, 2, 0.3, 3, 0.6, 4, 1, 5, 1.6, 6, 2.2, 7, 3, 0.8],
                8, ['match', ['get', 'StreamOrde'], 1, 0.4, 2, 0.6, 3, 1, 4, 1.5, 5, 2.2, 6, 3, 7, 4, 1.2],
                12, ['match', ['get', 'StreamOrde'], 1, 0.8, 2, 1.2, 3, 2, 4, 3, 5, 4, 6, 5.5, 7, 7, 2],
                14, ['match', ['get', 'StreamOrde'], 1, 1, 2, 1.5, 3, 2.5, 4, 3.5, 5, 5, 6, 7, 7, 9, 2.5]
              ],
              'line-opacity': 0.8
            }
          }, firstMarkerLayer);
        } catch (e) {}
      } else {
        map.setLayoutProperty('waterways-flowlines', 'visibility', 'visible');
      }

      if (!map.getLayer('waterways-flowlines-label')) {
        map.addLayer({
          id: 'waterways-flowlines-label', type: 'symbol', source: 'waterways', 'source-layer': 'flowlines', minzoom: 10,
          filter: ['has', 'GNIS_Name'],
          layout: {
            'symbol-placement': 'line', 'text-field': ['get', 'GNIS_Name'],
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 10, 10, 14, 12],
            'text-max-angle': 30, 'visibility': 'visible'
          },
          paint: { 'text-color': '#0369A1', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 }
        });
      } else {
        map.setLayoutProperty('waterways-flowlines-label', 'visibility', 'visible');
      }

      if (!map.getLayer('waterway-highlight')) {
        map.addLayer({
          id: 'waterway-highlight', type: 'line', source: 'waterways', 'source-layer': 'flowlines',
          filter: ['==', 'Permanent_Identifier', ''],
          paint: { 'line-color': '#F97316', 'line-width': 5, 'line-opacity': 0.9 },
          layout: { 'visibility': 'visible' }
        });
      }
    }
  } catch (error) {}
}
